import { ButtonType, ButtonWidget, FlyoutListMenuWidget, MenuButtonWidget, SplitButtonWidget } from '@tableau/widgets';
import React from 'react';

import { editSelfServiceSchedule } from '~src/app/sagas/spaces/ScheduleMenuSagas';
import { IGetUserIdentityMigrationBackgroundJobResult } from '~src/authentication/requests/GetUserIdentityMigrationBackgroundJobRequest';
import { UserIdentityConflictsService } from '~src/authentication/services/UserIdentityConflictsService';
import { Messages } from '~src/codegen/Localize';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { ISchedule } from '~src/schedule/types/ISchedule';
import { Schedules } from '~src/ts/resources/Schedules';
import { ToasterService, ToastType } from '~src/ts/services/ToasterService';


enum MigrationJobActionTypes {
  ENABLE = 'ENABLE',
  DISABLE = 'DISABLE',
  RESCHEDULE = 'RESCHEDULE',
  RUN_NOW = 'RUN_NOW',
}

interface IdentityMigartionJobAction {
  type: MigrationJobActionTypes;
  label: string;
  performAction: () => Promise<IResult>;
  isDisabled: boolean;
  successMessage: string;
  errorMessage: string;
}

function showErrorToast(message: string) {
  ToasterService.getInstance().pop({
    type: ToastType.Error,
    text: message,
  });
}

function showSuccessToast(message: string) {
  ToasterService.getInstance().pop({
    type: ToastType.Success,
    text: message,
  });
}

export function RescheduleIdentityMigrationJobButton() {
  const [identityMigrationSchedule, setIdentityMigrationSchedule] = React.useState<ISchedule>(null);
  const [isBackgroundJobEnabled, setIsBackgroundJobEnabled] = React.useState<boolean>(true);

  // Note this needs to be inside the component because outside it would complain about us referencing Messages
  // before it is loaded.
  const actions: IdentityMigartionJobAction[] = [
    {
      type: MigrationJobActionTypes.RESCHEDULE,
      label: Messages.identityMigration_BackgroundJob_Action_Reschedule(),
      performAction: () => editSelfServiceSchedule({ ...identityMigrationSchedule, contentType: 'schedule' }, true),
      isDisabled: false,
      successMessage: Messages.identityMigration_BackgroundJob_Action_Reschedule_Success(),
      errorMessage: Messages.errorUnknown(),
    },
    {
      type: MigrationJobActionTypes.ENABLE,
      label: Messages.identityMigration_BackgroundJob_Action_Enable(),
      performAction: () => Schedules.instance.enable([identityMigrationSchedule.id]),
      isDisabled: isBackgroundJobEnabled,
      successMessage: Messages.identityMigration_BackgroundJob_Action_Enable_Success(),
      errorMessage: Messages.errorUnknown(),
    },
    {
      type: MigrationJobActionTypes.DISABLE,
      label: Messages.identityMigration_BackgroundJob_Action_Disable(),
      performAction: () => Schedules.instance.disable([identityMigrationSchedule.id]),
      isDisabled: !isBackgroundJobEnabled,
      successMessage: Messages.identityMigration_BackgroundJob_Action_Disable_Success(),
      errorMessage: Messages.errorUnknown(),
    },
    {
      type: MigrationJobActionTypes.RUN_NOW,
      label: Messages.identityMigration_BackgroundJob_Action_RunNow(),
      performAction: () => UserIdentityConflictsService.runUserIdentityMigrationBackgroundJobNow(),
      isDisabled: false,
      successMessage: Messages.identityMigration_BackgroundJob_Action_RunNow_Success(),
      errorMessage: Messages.errorUnknown(),
    },
  ];

  function refreshBackgroundJobInfo() {
    UserIdentityConflictsService.fetchUserIdentityMigrationBackgroundJob().then((result: IGetUserIdentityMigrationBackgroundJobResult) => {
      if (result.errors) {
        // We fail to find the background job, there's no recovery path available, just a default error message.
        showErrorToast(Messages.errorUnknown());
        return;
      }
      setIdentityMigrationSchedule(result.schedule);
      setIsBackgroundJobEnabled(result.isEnabled);
    }).catch(() => {
      showErrorToast(Messages.errorUnknown());
    });
  }

  React.useEffect(() => {
    refreshBackgroundJobInfo();
  }, []);

  function onActionClicked(action: IdentityMigartionJobAction) {
    action.performAction().then((result: IResult) => {
      if (result && result.errors && result.errors[0]) {
        // Action failed, no possible recovery.
        showErrorToast(action.errorMessage);
        return;
      }
      showSuccessToast(action.successMessage);
      refreshBackgroundJobInfo();
    });
  }

  const topButtonAction = actions[0];
  const remainingActions = actions.slice(1);

  return <SplitButtonWidget
      button={
        <ButtonWidget
          buttonType={ButtonType.Primary}
          children={topButtonAction.label}
          onClick={() => onActionClicked(topButtonAction)}
          testId=''
        />}
      menuButton={
        <DropdownButtonWithActions
          aria-label={Messages.identityMigration_BackgroundJob_DropDownButton()}
          actions={remainingActions}
          onActionClicked={onActionClicked}
      />}
    />;
}

interface DropdownButtonWithActionsProps {
  actions: IdentityMigartionJobAction[];
  onActionClicked: (action: IdentityMigartionJobAction) => void;
}

export function DropdownButtonWithActions(props: DropdownButtonWithActionsProps & typeof MenuButtonWidget.defaultProps) {
  const [isOpen, setOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const menuId = 'menubutton-menu-id';

  const { actions, onActionClicked, ...buttonProps } = props;

  function onSelect(value: string) {
    setOpen(false);
    const action = actions.find(x => x.type === value);
    onActionClicked(action);
  }

  return <>
    <MenuButtonWidget
      aria-controls={menuId}
      isOpen={isOpen}
      onClick={() => setOpen(!isOpen)}
      ref={setAnchorEl}
      testId='identity-migration-bg-job-action-list-arrow'
      {...buttonProps}
    />
    {isOpen &&
      <FlyoutListMenuWidget
        anchorEl={anchorEl!}
        allowAutoPosition
        id={menuId}
        menuItemInfos={actions.filter(x => !x.isDisabled).map(x => ({ displayValue: x.label, value: x.type}))}
        onSelect={onSelect}
        requestCloseAll={() => setOpen(false)}
        menuStyle={{ minWidth: anchorEl!.getBoundingClientRect().width }}
        subMenuAllowAutoPosition
        testId='identity-migration-bg-job-action-list'
        toggleEl={anchorEl!}
      />
    }
  </>;
}
